import type { GbaButton, MgbaCore } from "./types";
import layout from "./keymap.json";
import { onPreferencesApplied, setPref } from "@common/preferences";

// The keyboard layout, for every console and both apps, is keymap.json:
// KeyboardEvent.code names (which winit's KeyCode shares) against each
// console's buttons. The desktop game process reads the same file
// (desktop/emu/src/keymap.rs), so a key means the same thing everywhere.
// The player's own choices sit on top as overrides, kept in this browser
// (or the app); the app writes them out for the game process at each
// launch. Each engine below turns the result into its own bindings.

export type Layout = keyof typeof layout.consoles;

/** The layout a console plays with: the Game Boys share one. */
export function layoutOf(system: string): Layout {
  return system in layout.consoles ? (system as Layout) : "gb";
}

export interface Binding {
  button: string;
  keys: string[];
}

/** A console's buttons with the keys the player chose; a whole console is absent when untouched. */
export type Overrides = Partial<Record<Layout, Record<string, string[]>>> & { turbo?: string };

const STORAGE_KEY = "pocket-play.keymap";
const listeners = new Set<() => void>();
let overrides: Overrides = read();

function read(): Overrides {
  try {
    return (JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}") as Overrides) ?? {};
  } catch {
    return {};
  }
}

function write(next: Overrides) {
  overrides = next;
  try {
    setPref(STORAGE_KEY, Object.keys(next).length ? JSON.stringify(next) : null);
  } catch {
    // Kept for this page at least.
  }
  for (const listener of listeners) listener();
}

// The account's layout can replace this browser's at load, after this
// module has read it.
onPreferencesApplied(() => {
  overrides = read();
  for (const listener of listeners) listener();
});

/** Runs `listener` whenever a binding changes; returns the way to stop. */
export function onKeymapChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The player's choices, for the desktop game process. */
export const currentOverrides = (): Overrides => overrides;

function defaults(system: Layout): Binding[] {
  return [...Object.entries(layout.directions).map(([button, keys]) => ({ button, keys })), ...layout.consoles[system].buttons];
}

/** Directions first, then the console's buttons, in the order they're shown, with the player's keys. */
export function bindings(system: Layout): Binding[] {
  const chosen = overrides[system] ?? {};
  return defaults(system).map(({ button, keys }) => ({ button, keys: chosen[button] ?? keys }));
}

/** What the directions move, for the help: "D-pad" or "analog stick". */
export const movesOf = (system: Layout): string => layout.consoles[system].moves;

const DEFAULT_TURBO = Object.entries(layout.hotkeys).find(([, action]) => action === "turbo")![0];

/** Held to fast-forward, in every engine. */
export const turboKey = (): string => overrides.turbo ?? DEFAULT_TURBO;

/** Whether a console's keys, or the turbo key, differ from the shared layout. */
export const isCustomised = (system: Layout): boolean => system in overrides || "turbo" in overrides;

/**
 * Puts `code` on slot `slot` of a button (or on turbo, with button null). A
 * key does one thing per console, so it leaves any other button it was on.
 */
export function assignKey(system: Layout, button: string | null, slot: number, code: string) {
  const next: Overrides = { ...overrides, [system]: Object.fromEntries(bindings(system).map((b) => [b.button, [...b.keys]])) };
  const console = next[system]!;
  for (const keys of Object.values(console)) {
    const at = keys.indexOf(code);
    if (at >= 0) keys.splice(at, 1);
  }
  if (button === null) next.turbo = code;
  else {
    if (turboKey() === code) next.turbo = "";
    const keys = console[button];
    if (slot < keys.length) keys[slot] = code;
    else keys.push(code);
  }
  // A button with no key left gets nothing; the player can see and fix it.
  write(next);
}

/** Back to the shared layout for one console (and the turbo key). */
export function resetKeys(system: Layout) {
  const next = { ...overrides };
  delete next[system];
  delete next.turbo;
  write(next);
}

/** Set while the Keyboard panel waits for a key, so the game ignores it. */
export let capturing = false;
export const setCapturing = (on: boolean) => {
  capturing = on;
};

/** A key code as the help shows it: KeyX → X, ArrowUp → ↑, Slash → /. */
export function keyLabel(code: string): string {
  const named: Record<string, string> = {
    ArrowUp: "↑",
    ArrowDown: "↓",
    ArrowLeft: "←",
    ArrowRight: "→",
    Slash: "/",
    Period: ".",
    Comma: ",",
    Semicolon: ";",
    Quote: "'",
    BracketLeft: "[",
    BracketRight: "]",
    Backslash: "\\",
    Minus: "-",
    Equal: "=",
    Backquote: "`",
  };
  return named[code] ?? code.replace(/^(Key|Digit|Numpad)/, (p) => (p === "Numpad" ? "Num " : ""));
}

// mGBA takes bindings by SDL key name, one per button (bindKey replaces the
// previous one), so a button's first key goes through it and the rest
// through buttonPress below, which ORs into the same bitmask.
function sdlKeyName(code: string): string {
  const named: Record<string, string> = { Enter: "Return", Slash: "/", Period: ".", Comma: ",", Semicolon: ";", Quote: "'", Minus: "-", Equal: "=" };
  return named[code] ?? code.replace(/^Key/, "").replace(/^Arrow/, "").replace(/^Digit/, "");
}

const gb = () => bindings("gb") as { button: GbaButton; keys: string[] }[];

/** Second and later keys for each Game Boy button, by key code. */
export const extraKeys = (): Record<string, GbaButton> =>
  Object.fromEntries(gb().flatMap(({ button, keys }) => keys.slice(1).map((code) => [code, button])));

// loadGame re-initializes the core's input map, so call this after every
// load, and again when a binding changes. A button with no key is bound to
// nothing SDL names.
export function applyKeymap(core: MgbaCore) {
  for (const { button, keys } of gb()) core.bindKey(keys.length ? sdlKeyName(keys[0]) : "Unknown", button);
}

// Keys the core's own SDL listener doesn't handle the way we want. It runs
// in the bubble phase on window, so a capture listener gets there first.
//
// Fast-forward: setFastForwardMultiplier is a persistent speed, not a hold
// speed, and the core's own F key is a fixed 2x that only works while the
// multiplier is 1. So the turbo key down sets the multiplier and up restores
// it, and F is dropped so there is one fast-forward key. Blur releases
// everything in case a keyup is lost to a focus change.
// `resting` is the speed to return to when the key is released or the window
// loses focus: 1, or the chosen multiplier while the fast-forward toggle is on.
export function bindKeyboardExtras(core: MgbaCore, multiplier: () => number, resting: () => number): () => void {
  const onKey = (e: KeyboardEvent) => {
    if (e.metaKey || e.ctrlKey || e.altKey || capturing) return;
    if ((e.target as Element | null)?.matches?.("input, select, textarea")) return;
    const down = e.type === "keydown";
    const button = extraKeys()[e.code];
    if (button) {
      e.preventDefault();
      if (!e.repeat) (down ? core.buttonPress : core.buttonUnpress)(button);
    } else if (e.code === "KeyF") {
      e.stopImmediatePropagation();
    } else if (e.code === turboKey()) {
      e.preventDefault();
      if (!e.repeat) core.setFastForwardMultiplier(down ? multiplier() : resting());
    }
  };
  const release = () => {
    core.setFastForwardMultiplier(resting());
    for (const button of Object.values(extraKeys())) core.buttonUnpress(button);
  };
  const rebind = () => {
    release();
    applyKeymap(core);
  };
  window.addEventListener("keydown", onKey, true);
  window.addEventListener("keyup", onKey, true);
  window.addEventListener("blur", release);
  const stop = onKeymapChange(rebind);
  return () => {
    window.removeEventListener("keydown", onKey, true);
    window.removeEventListener("keyup", onKey, true);
    window.removeEventListener("blur", release);
    stop();
  };
}
